import {
  IArchive, ICountry, IField, ISite, IWell, IWellbore, IDesign, HierarchyLevelType, IObject, INode,
} from 'common/interfaces/hierarchy-entities';
import _ from 'es-toolkit/compat';
import { t } from 'i18next';
import translations from 'client/internationalization/translation-map.json';
import {
  IHierarchyTreeItem,
} from './hierarchy.interfaces';

export const combineHierarchy = (
  archives: IArchive[],
  countries: ICountry[],
  fields: IField[],
  sites: ISite[],
  wells: IWell[],
  wellbores: IWellbore[],
  designs: IDesign[],
) => {
  const items: IHierarchyTreeItem[] = [];
  archives.forEach((archive) => {
    items.push({
      ...archive,
      id: archive.archiveid,
      metadata: {
        created: archive.created,
        updated: archive.updated,
      },
      nodeId: '',
      parentId: '',
      entityType: HierarchyLevelType.Archive,
    });
  });
  countries.forEach((country) => {
    items.push({
      ...country,
      id: country.countryid,
      nodeId: country.nodeid,
      parentId: country.archiveid,
      entityType: HierarchyLevelType.Country,
    });
  });
  fields.forEach((field) => {
    items.push({
      ...field,
      id: field.fieldid,
      nodeId: field.nodeid,
      parentId: field.countryid,
      entityType: HierarchyLevelType.Field,
    });
  });
  sites.forEach((site) => {
    items.push({
      ...site,
      id: site.siteid,
      nodeId: site.nodeid,
      parentId: site.fieldid,
      entityType: HierarchyLevelType.Site,
    });
  });
  wells.forEach((well) => {
    items.push({
      ...well,
      id: well.wellid,
      nodeId: well.nodeid,
      parentId: well.siteid,
      entityType: HierarchyLevelType.Well,
    });
  });
  wellbores.forEach((wellbore) => {
    items.push({
      ...wellbore,
      id: wellbore.wellboreid,
      nodeId: wellbore.nodeid,
      parentId: wellbore.wellid,
      entityType: HierarchyLevelType.Wellbore,
    });
  });
  designs.forEach((design) => {
    items.push({
      ...design,
      id: design.designid,
      parentId: design.wellboreid,
      entityType: HierarchyLevelType.Design,
    });
  });
  return items;
};

export const generateHierarchyTree = (
  hierarchy: IHierarchyTreeItem[],
  setActiveItem: (item: IHierarchyTreeItem) => void,
): { [id: string]: IHierarchyTreeItem } => {
  const items: { [id: string]: IHierarchyTreeItem } = {};

  hierarchy.forEach((item) => {
    items[item.id] = {
      ..._.omit(item, ['metadata']),
      createdBy: item.metadata?.createdBy,
      id: item.id,
      key: `hierarchy-tree-${item.id}`,
      name: item.name,
      entityType: item.entityType,
      parent: item.entityType === HierarchyLevelType.Archive ? 0 : item.parentId || -1,
      nodeId: item.nodeId,
      onClick: () => {
        const { onClick, ...itemClean } = item;
        setActiveItem(itemClean);
      },
    } as IHierarchyTreeItem;
  });

  return items;
};

export const dataObjectToTreeItem = (dataObject: IObject): IHierarchyTreeItem => ({
  ...dataObject,
  id: dataObject.objectid,
  key: dataObject.objectid,
  name: dataObject.name,
  entityType: HierarchyLevelType.Object,
  parent: dataObject.nodeid,
});

export const nodeToTreeItem = (node: INode): IHierarchyTreeItem => ({
  ...node,
  id: node.nodeid,
  key: node.nodeid,
  name: node.name,
  entityType: HierarchyLevelType.Node,
  parent: node.parentid || node.parent?.id || -1,
});

const ENTITIES_LEVELS = [
  HierarchyLevelType.Archive, HierarchyLevelType.Country, HierarchyLevelType.Field, HierarchyLevelType.Site,
  HierarchyLevelType.Well, HierarchyLevelType.Wellbore, HierarchyLevelType.Design, HierarchyLevelType.Node, HierarchyLevelType.Object,
];

const isEntityLevelHigherThen = (entityType: HierarchyLevelType, testEntityType: HierarchyLevelType): boolean => {
  const entityLevel = ENTITIES_LEVELS.indexOf(entityType);
  const testLevel = ENTITIES_LEVELS.indexOf(testEntityType);
  return entityLevel < testLevel;
};

export const groupHierarchyTreeBy = (array: IHierarchyTreeItem[], groupLevel: HierarchyLevelType) => {
  const grouped: IHierarchyTreeItem[] = [];
  array.forEach((item: IHierarchyTreeItem) => {
    if (item.entityType === HierarchyLevelType.Node) {
      grouped.push(item);
      return;
    }
    let { parent } = item;
    if (item.entityType === groupLevel) {
      parent = 0;
    } else if (isEntityLevelHigherThen(item.entityType, groupLevel)) {
      parent = -1;
    }
    grouped.push({
      ...item,
      parent,
    });
  });
  return grouped;
};

export const filterHierarchy = (
  array: IHierarchyTreeItem[],
  query: string, topLevel: HierarchyLevelType,
  filter?: { entityType: HierarchyLevelType, key: keyof IHierarchyTreeItem, values: string[] }
) => {
  if (topLevel === filter?.entityType  && filter?.key && filter?.values?.length > 0) {
    array = array.filter((item) => filter.values.includes(item[filter.key]));
  }
  if (!query) {
    return array;
  }
  const filterText = query.toLowerCase();
  return array.filter((item) => (item.entityType !== topLevel) || (item.name.toLowerCase().includes(filterText)));
};

export const getChildHierarchyEntityType = (parentEntity: HierarchyLevelType): HierarchyLevelType | null => {
  const parentLevel = ENTITIES_LEVELS.indexOf(parentEntity);
  if (parentLevel < 0) return null;
  if (parentLevel === ENTITIES_LEVELS.length - 1) return null;
  const entityType = ENTITIES_LEVELS[parentLevel + 1];
  return entityType;
};

export const getParentHierarchyEntityType = (childEntity: HierarchyLevelType): HierarchyLevelType | null => {
  const childLevel = ENTITIES_LEVELS.indexOf(childEntity);
  if (childLevel <= 0) return null;
  const entityType = ENTITIES_LEVELS[childLevel - 1];
  return entityType;
};

export const getHierarchyLevelSliceName = (entityType: HierarchyLevelType): string => {
  switch (entityType) {
    case HierarchyLevelType.Node: return 'Catalogue';
    case HierarchyLevelType.Object: return 'File';
  }
  return entityType[0].toUpperCase() + entityType.substring(1);
};

export const hierarchyLevelTypeName = {
  [HierarchyLevelType.Archive]: t(translations.archive),
  [HierarchyLevelType.Country]: t(translations.country),
  [HierarchyLevelType.Field]: t(translations.field),
  [HierarchyLevelType.Site]: t(translations.site),
  [HierarchyLevelType.Well]: t(translations.well),
  [HierarchyLevelType.Wellbore]: t(translations.wellbore),
  [HierarchyLevelType.Design]: t(translations.design),
  [HierarchyLevelType.Node]: t(translations.catalogue),
  [HierarchyLevelType.Object]: t(translations.file),
};
export const hierarchyLevelTypeNamePlural = {
  [HierarchyLevelType.Archive]: t(translations.archives),
  [HierarchyLevelType.Country]: t(translations.countries),
  [HierarchyLevelType.Field]: t(translations.fields),
  [HierarchyLevelType.Site]: t(translations.sites),
  [HierarchyLevelType.Well]: t(translations.wells),
  [HierarchyLevelType.Wellbore]: t(translations.wellbores),
  [HierarchyLevelType.Design]: t(translations.designs),
  [HierarchyLevelType.Node]: t(translations.catalogues),
  [HierarchyLevelType.Object]: t(translations.files),
};
export const hierarchyTypeAddLabel = {
  [HierarchyLevelType.Archive]: `${t(translations.add)} ${t(translations.archive)}`,
  [HierarchyLevelType.Country]: `${t(translations.add)} ${t(translations.country)}`,
  [HierarchyLevelType.Field]: `${t(translations.add)} ${t(translations.field)}`,
  [HierarchyLevelType.Site]: `${t(translations.add)} ${t(translations.site)}`,
  [HierarchyLevelType.Well]: `${t(translations.add)} ${t(translations.well)}`,
  [HierarchyLevelType.Wellbore]: `${t(translations.add)} ${t(translations.wellbore)}`,
  [HierarchyLevelType.Design]: `${t(translations.add)} ${t(translations.design)}`,
  [HierarchyLevelType.Node]: `${t(translations.add)} ${t(translations.catalogue)}`,
  [HierarchyLevelType.Object]: `${t(translations.add)} ${t(translations.file)}`,
};
export const hierarchyTypeNewLabel = {
  [HierarchyLevelType.Archive]: `${t(translations.new)} ${t(translations.archive)}`,
  [HierarchyLevelType.Country]: `${t(translations.new)} ${t(translations.country)}`,
  [HierarchyLevelType.Field]: `${t(translations.new)} ${t(translations.field)}`,
  [HierarchyLevelType.Site]: `${t(translations.new)} ${t(translations.site)}`,
  [HierarchyLevelType.Well]: `${t(translations.new)} ${t(translations.well)}`,
  [HierarchyLevelType.Wellbore]: `${t(translations.new)} ${t(translations.wellbore)}`,
  [HierarchyLevelType.Design]: `${t(translations.new)} ${t(translations.design)}`,
  [HierarchyLevelType.Node]: `${t(translations.new)} ${t(translations.catalogue)}`,
  [HierarchyLevelType.Object]: `${t(translations.new)} ${t(translations.file)}`,
};
export const hierarchyTypeEditLabel = {
  [HierarchyLevelType.Archive]: `${t(translations.edit)} ${t(translations.archive)}`,
  [HierarchyLevelType.Country]: `${t(translations.edit)} ${t(translations.country)}`,
  [HierarchyLevelType.Field]: `${t(translations.edit)} ${t(translations.field)}`,
  [HierarchyLevelType.Site]: `${t(translations.edit)} ${t(translations.site)}`,
  [HierarchyLevelType.Well]: `${t(translations.edit)} ${t(translations.well)}`,
  [HierarchyLevelType.Wellbore]: `${t(translations.edit)} ${t(translations.wellbore)}`,
  [HierarchyLevelType.Design]: `${t(translations.edit)} ${t(translations.design)}`,
  [HierarchyLevelType.Node]: `${t(translations.edit)} ${t(translations.catalogue)}`,
  [HierarchyLevelType.Object]: `${t(translations.edit)} ${t(translations.file)}`,
};

const POSSIBLE_NODE_PARENTS = [HierarchyLevelType.Country, HierarchyLevelType.Field, HierarchyLevelType.Site, HierarchyLevelType.Well, HierarchyLevelType.Wellbore, HierarchyLevelType.Node];

export const canNodeBeAddedToHierarchyItem = (item: IHierarchyTreeItem): boolean => {
  if (!POSSIBLE_NODE_PARENTS.includes(item.entityType)) return false;
  if (item.entityType === HierarchyLevelType.Node) return true;
  return !item.nodeId;
};

export const generateDesignHierarchyData = (
  itemId: string | null | undefined,
  hierarchyTree: IHierarchyTreeItem[],
): { [key: string ]: { id: string; name: string } } => {
  if (!itemId) return {};

  const itemData = hierarchyTree.find((i) => i.id === itemId);
  if (!itemData) return {};

  const parentHierarchy = generateDesignHierarchyData(itemData.parentId, hierarchyTree);

  return ({
    ...parentHierarchy,
    [itemData.entityType]: {
      id: itemData.id,
      name: itemData.name,
    },
  });
};
